import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLang } from '../LanguageContext';
import StrainCard from '../components/StrainCard';
import DisplayModeToggle from '../components/DisplayModeToggle';
import ImageUploadInput from '../components/ImageUploadInput';
import { LABEL_MODES, useLabelMode } from '../hooks/useLabelMode';
import {
  validateMediaFiles,
  uploadMedia,
  getMediaErrorMessage,
} from '../utils/imageUpload';
import {
  normalizeMediaList,
  ensureMainMedia,
  setMainMediaByUrl,
  removeMediaByUrl,
  addMediaItem,
} from '../utils/media';

class StrainsAdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('StrainsAdminPage crashed', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mt-3" style={{ maxWidth: 700 }}>
          <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
            <div>{this.props.fallbackMessage}</div>
            <button type="button" className="btn btn-sm btn-light" onClick={this.handleRetry}>
              {this.props.retryLabel}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function StrainsAdminPageContent() {
  const { t } = useLang();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const decodeRole = (tok) => {
    if (!tok) return null;
    try {
      return JSON.parse(atob(tok.split('.')[1])).role;
    } catch {
      return null;
    }
  };
  const role = decodeRole(token);
  const [strains, setStrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [labelMode, setLabelMode] = useLabelMode();
  const [mode, setMode] = useState('medium');
  const [form, setForm] = useState({
    name: '',
    slug: '',
    genetics: '',
    geneticsType: '',
    geneticsDominance: '',
    description: '',
    shortDescription: '',
    thcPercent: '',
    cbdPercent: '',
    floweringWeeks: '',
    floweringCategory: '',
    yield: '',
    plantHeight: '',
    terpenes: [],
    aromaFlavor: [],
    effectDirection: '',
    effectSensations: [],
    status: '',
    uniqueTrait: '',
    positioning: '',
    recommendedTime: '',
    targetAudience: '',
    extendedLabReports: [],
    extendedFullCannabinoidProfile: '',
    extendedSecondaryTerpenes: [],
    extendedCultivationNotes: '',
    extendedInternalCommercialNotes: '',
    images: [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [edit, setEdit] = useState(null);

  const parseTags = (value, max) =>
    value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item)
      .slice(0, max);

  const formatTags = (items) => (Array.isArray(items) ? items.join(', ') : '');

  const handleTagChange = (key, max) => (e) => {
    const next = parseTags(e.target.value, max);
    setForm((prev) => ({ ...prev, [key]: next }));
  };

  const fetchStrains = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setStrains([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/admin/strains', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 204) {
        setStrains([]);
        return;
      }

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        const messageText = errBody?.error || t('strainsLoadError');
        setError(messageText);
        setStrains([]);
        return;
      }

      const data = await res.json().catch(() => []);
      setStrains(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch strains', err);
      setError(t('strainsLoadError'));
      setStrains([]);
    } finally {
      setLoading(false);
    }
  }, [t, token]);

  useEffect(() => {
    fetchStrains();
  }, [fetchStrains]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) {
      setMessage('Please fill required fields');
      return;
    }
    setSaving(true);
    setMessage('');
    const mainImage = form.images.find((img) => img.isMain);
    const body = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      genetics: form.genetics.trim(),
      geneticsType: form.geneticsType || '',
      geneticsDominance: form.geneticsDominance.trim(),
      description: form.description.trim(),
      shortDescription: form.shortDescription.trim(),
      thcPercent: form.thcPercent.trim(),
      cbdPercent: form.cbdPercent.trim(),
      floweringWeeks: form.floweringWeeks ? Number(form.floweringWeeks) : null,
      floweringCategory: form.floweringCategory || '',
      yield: form.yield || '',
      plantHeight: form.plantHeight || '',
      terpenes: Array.isArray(form.terpenes) ? form.terpenes.slice(0, 6) : [],
      aromaFlavor: Array.isArray(form.aromaFlavor) ? form.aromaFlavor.slice(0, 6) : [],
      effectDirection: form.effectDirection || '',
      effectSensations: Array.isArray(form.effectSensations)
        ? form.effectSensations.slice(0, 5)
        : [],
      status: form.status || '',
      uniqueTrait: form.uniqueTrait.trim(),
      positioning: form.positioning || '',
      recommendedTime: form.recommendedTime || '',
      targetAudience: form.targetAudience || '',
      extended: {
        labReports: Array.isArray(form.extendedLabReports)
          ? form.extendedLabReports.slice(0, 10)
          : [],
        fullCannabinoidProfile: form.extendedFullCannabinoidProfile.trim(),
        secondaryTerpenes: Array.isArray(form.extendedSecondaryTerpenes)
          ? form.extendedSecondaryTerpenes.slice(0, 12)
          : [],
        cultivationNotes: form.extendedCultivationNotes.trim(),
        internalCommercialNotes: form.extendedInternalCommercialNotes.trim(),
      },
      images: form.images.slice(0, 5),
      photo: mainImage?.url || '',
      photoPublicId: mainImage?.publicId || '',
    };
    const method = edit ? 'PUT' : 'POST';
    const url = edit ? `/api/admin/strains/${edit}` : '/api/admin/strains';
    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessage(err.error || 'Error');
      } else {
        setMessage('Saved');
        setForm({
          name: '',
          slug: '',
          genetics: '',
          geneticsType: '',
          geneticsDominance: '',
          description: '',
          shortDescription: '',
          thcPercent: '',
          cbdPercent: '',
          floweringWeeks: '',
          floweringCategory: '',
          yield: '',
          plantHeight: '',
          terpenes: [],
          aromaFlavor: [],
          effectDirection: '',
          effectSensations: [],
          status: '',
          uniqueTrait: '',
          positioning: '',
          recommendedTime: '',
          targetAudience: '',
          extendedLabReports: [],
          extendedFullCannabinoidProfile: '',
          extendedSecondaryTerpenes: [],
          extendedCultivationNotes: '',
          extendedInternalCommercialNotes: '',
          images: [],
        });
        setEdit(null);
        await fetchStrains();
      }
    } catch (err) {
      setMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (s) => {
    setEdit(s._id);
    let imgs = [];
    if (Array.isArray(s.images) && s.images.length) {
      imgs = ensureMainMedia(normalizeMediaList(s.images));
    } else {
      const fallback = [];
      if (s.photo) {
        fallback.push({
          url: s.photo,
          thumbnailUrl: s.photo,
          publicId: s.photoPublicId,
          isMain: true,
        });
      }
      const secondary = Array.isArray(s.imageUrls) ? s.imageUrls : [];
      const publicIds = Array.isArray(s.imagePublicIds) ? s.imagePublicIds : [];
      fallback.push(
        ...secondary.map((u, idx) => ({
          url: u,
          thumbnailUrl: u,
          publicId: publicIds[idx],
          isMain: false,
        }))
      );
      imgs = ensureMainMedia(normalizeMediaList(fallback));
    }
    setForm({
      name: s.name || '',
      slug: s.slug || '',
      genetics: s.genetics || '',
      geneticsType: s.geneticsType || '',
      geneticsDominance: s.geneticsDominance || '',
      description: s.description || '',
      shortDescription: s.shortDescription || '',
      thcPercent: s.thcPercent || '',
      cbdPercent: s.cbdPercent || '',
      floweringWeeks: s.floweringWeeks ?? '',
      floweringCategory: s.floweringCategory || '',
      yield: s.yield || '',
      plantHeight: s.plantHeight || '',
      terpenes: Array.isArray(s.terpenes) ? s.terpenes : [],
      aromaFlavor: Array.isArray(s.aromaFlavor) ? s.aromaFlavor : [],
      effectDirection: s.effectDirection || '',
      effectSensations: Array.isArray(s.effectSensations) ? s.effectSensations : [],
      status: s.status || '',
      uniqueTrait: s.uniqueTrait || '',
      positioning: s.positioning || '',
      recommendedTime: s.recommendedTime || '',
      targetAudience: s.targetAudience || '',
      extendedLabReports: Array.isArray(s.extended?.labReports) ? s.extended.labReports : [],
      extendedFullCannabinoidProfile: s.extended?.fullCannabinoidProfile || '',
      extendedSecondaryTerpenes: Array.isArray(s.extended?.secondaryTerpenes)
        ? s.extended.secondaryTerpenes
        : [],
      extendedCultivationNotes: s.extended?.cultivationNotes || '',
      extendedInternalCommercialNotes: s.extended?.internalCommercialNotes || '',
      images: imgs,
    });
  };

  const del = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    await fetch(`/api/admin/strains/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    await fetchStrains();
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remainingSlots = 5 - form.images.length;
    if (remainingSlots <= 0) {
      setMessage(t('maxImages'));
      e.target.value = '';
      return;
    }
    const filesToUpload = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      setMessage(t('maxImages'));
    }
    const err = validateMediaFiles(filesToUpload);
    if (err) {
      setMessage(getMediaErrorMessage(err, t));
      e.target.value = '';
      return;
    }
    setUploading(true);
    setMessage('');
    for (const file of filesToUpload) {
      try {
        const d = await uploadMedia(file, token);
        setForm((f) => ({
          ...f,
          images: addMediaItem(f.images, {
            url: d.url,
            publicId: d.publicId,
            type: d.type,
            thumbnailUrl: d.thumbnailUrl,
          }),
        }));
      } catch (e2) {
        setMessage(getMediaErrorMessage(e2.message, t));
        break;
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removePhoto = (url) => {
    setForm((f) => ({
      ...f,
      images: removeMediaByUrl(f.images, url),
    }));
  };

  const setMainImage = (url) => {
    setForm((f) => ({
      ...f,
      images: setMainMediaByUrl(f.images, url),
    }));
  };

  const filtered = useMemo(() => {
    const safeStrains = Array.isArray(strains) ? strains : [];
    const lowerSearch = search.toLowerCase();

    const result = safeStrains.filter((s) => {
      const name = (s?.name || '').toLowerCase();
      const slugValue = (s?.slug || '').toLowerCase();
      if (!lowerSearch) return true;
      return name.includes(lowerSearch) || slugValue.includes(lowerSearch);
    });

    return result.sort((a, b) => {
      const nameA = (a?.name || '').toString();
      const nameB = (b?.name || '').toString();
      const slugA = (a?.slug || '').toString();
      const slugB = (b?.slug || '').toString();

      if (sort === 'name') return nameA.localeCompare(nameB);
      return slugA.localeCompare(slugB);
    });
  }, [search, sort, strains]);

  if (!token || !(role === 'superuser' || role === 'admin')) {
    return (
      <div className="container mt-3">
        <p>{t('login')}...</p>
      </div>
    );
  }

  return (
    <div className="container mt-3" style={{ maxWidth: 700 }}>
      <h1 className="mb-3">{t('manageStrains')}</h1>
      {error && (
        <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-light" onClick={fetchStrains}>
            {t('retry')}
          </button>
        </div>
      )}
      {loading && (
        <div className="alert alert-info d-flex align-items-center" role="status">
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
          <span>{t('loading')}</span>
        </div>
      )}
      <div className="d-flex mb-3 align-items-center">
        <input
          className="form-control me-2"
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-select w-auto me-2"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="name">{t('byName')}</option>
          <option value="code">{t('byCode')}</option>
        </select>
        <select
          className="form-select w-auto me-2"
          value={labelMode}
          onChange={(e) => setLabelMode(e.target.value)}
        >
          <option value={LABEL_MODES.NAME_ONLY}>{t('labelModes.nameOnly')}</option>
          <option value={LABEL_MODES.NUMBER_ONLY}>{t('labelModes.numberOnly')}</option>
          <option value={LABEL_MODES.NAME_AND_NUMBER}>{t('labelModes.nameAndNumber')}</option>
        </select>
        <DisplayModeToggle mode={mode} setMode={setMode} />
      </div>
      <form onSubmit={handleSave} className="mb-3">
        <h5 className="mb-2">{t('passportCore')}</h5>
        <input className="form-control mb-2" placeholder={t('strain')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <input className="form-control mb-2" placeholder={t('slug')} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
        <select
          className="form-select mb-2"
          value={form.geneticsType}
          onChange={(e) => setForm({ ...form, geneticsType: e.target.value })}
        >
          <option value="">{t('geneticsType')}</option>
          <option value="Indica">Indica</option>
          <option value="Sativa">Sativa</option>
          <option value="Hybrid">Hybrid</option>
        </select>
        <input
          className="form-control mb-2"
          placeholder={t('geneticsDominance')}
          value={form.geneticsDominance}
          onChange={(e) => setForm({ ...form, geneticsDominance: e.target.value })}
        />
        <textarea
          className="form-control mb-2"
          placeholder={t('shortDescription')}
          value={form.shortDescription}
          onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
        />
        <input
          className="form-control mb-2"
          placeholder={t('thcPercent')}
          value={form.thcPercent}
          onChange={(e) => setForm({ ...form, thcPercent: e.target.value })}
        />
        <input
          className="form-control mb-2"
          placeholder={t('cbdPercent')}
          value={form.cbdPercent}
          onChange={(e) => setForm({ ...form, cbdPercent: e.target.value })}
        />
        <div className="row g-2 mb-2">
          <div className="col-6">
            <input
              className="form-control"
              type="number"
              min="0"
              placeholder={t('floweringWeeks')}
              value={form.floweringWeeks}
              onChange={(e) => setForm({ ...form, floweringWeeks: e.target.value })}
            />
          </div>
          <div className="col-6">
            <select
              className="form-select"
              value={form.floweringCategory}
              onChange={(e) => setForm({ ...form, floweringCategory: e.target.value })}
            >
              <option value="">{t('floweringCategory')}</option>
              <option value="Early">{t('floweringEarly')}</option>
              <option value="Medium">{t('floweringMedium')}</option>
              <option value="Late">{t('floweringLate')}</option>
            </select>
          </div>
        </div>
        <div className="row g-2 mb-2">
          <div className="col-6">
            <select
              className="form-select"
              value={form.yield}
              onChange={(e) => setForm({ ...form, yield: e.target.value })}
            >
              <option value="">{t('yield')}</option>
              <option value="Low">{t('yieldLow')}</option>
              <option value="Medium">{t('yieldMedium')}</option>
              <option value="High">{t('yieldHigh')}</option>
              <option value="Very High">{t('yieldVeryHigh')}</option>
            </select>
          </div>
          <div className="col-6">
            <select
              className="form-select"
              value={form.plantHeight}
              onChange={(e) => setForm({ ...form, plantHeight: e.target.value })}
            >
              <option value="">{t('plantHeight')}</option>
              <option value="Low">{t('heightLow')}</option>
              <option value="Medium">{t('heightMedium')}</option>
              <option value="High">{t('heightHigh')}</option>
            </select>
          </div>
        </div>
        <input
          className="form-control mb-2"
          placeholder={t('terpenes')}
          value={formatTags(form.terpenes)}
          onChange={handleTagChange('terpenes', 6)}
        />
        <input
          className="form-control mb-2"
          placeholder={t('aromaFlavor')}
          value={formatTags(form.aromaFlavor)}
          onChange={handleTagChange('aromaFlavor', 6)}
        />
        <div className="row g-2 mb-2">
          <div className="col-6">
            <select
              className="form-select"
              value={form.effectDirection}
              onChange={(e) => setForm({ ...form, effectDirection: e.target.value })}
            >
              <option value="">{t('effectDirection')}</option>
              <option value="Up">{t('effectUp')}</option>
              <option value="Neutral">{t('effectNeutral')}</option>
              <option value="Down">{t('effectDown')}</option>
            </select>
          </div>
          <div className="col-6">
            <input
              className="form-control"
              placeholder={t('effectSensations')}
              value={formatTags(form.effectSensations)}
              onChange={handleTagChange('effectSensations', 5)}
            />
          </div>
        </div>
        <div className="small text-muted mb-2">{t('tagLimit')}</div>
        <ImageUploadInput className="form-control mb-2" multiple onChange={handleFile} />
        {uploading && (
          <div className="mb-2">
            <span className="spinner-border spinner-border-sm me-2" />
            {t('photoUploading')}
          </div>
        )}
        <div className="small text-muted mb-2">{t('imageLimit')}</div>
        {form.images.map((img) => (
          <div key={img.url} className="mb-2 position-relative d-inline-block me-2" style={{ maxWidth: 120 }}>
            {img.type === 'video' ? (
              <video
                src={img.url}
                poster={img.thumbnailUrl || img.url}
                controls
                className="img-fluid"
                style={{ maxHeight: 120 }}
              />
            ) : (
              <img src={img.url} alt="preview" loading="lazy" className="img-fluid" />
            )}
            {img.isMain && (
              <span className="badge bg-primary position-absolute top-0 start-0">★</span>
            )}
            <button
              type="button"
              className="btn btn-sm btn-danger position-absolute top-0 end-0"
              onClick={() => removePhoto(img.url)}
            >
              &times;
            </button>
            {!img.isMain && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary position-absolute bottom-0 start-0"
                onClick={() => setMainImage(img.url)}
              >
                ★
              </button>
            )}
          </div>
        ))}
        <h5 className="mt-3 mb-2">{t('passportCorePlus')}</h5>
        <select
          className="form-select mb-2"
          value={form.status}
          onChange={(e) => setForm({ ...form, status: e.target.value })}
        >
          <option value="">{t('status')}</option>
          <option value="R&D">{t('statusRd')}</option>
          <option value="Test">{t('statusTest')}</option>
          <option value="Pre-commercial">{t('statusPreCommercial')}</option>
          <option value="Commercial">{t('statusCommercial')}</option>
        </select>
        <input
          className="form-control mb-2"
          placeholder={t('uniqueTrait')}
          value={form.uniqueTrait}
          onChange={(e) => setForm({ ...form, uniqueTrait: e.target.value })}
        />
        <select
          className="form-select mb-2"
          value={form.positioning}
          onChange={(e) => setForm({ ...form, positioning: e.target.value })}
        >
          <option value="">{t('positioning')}</option>
          <option value="Commercial">{t('positioningCommercial')}</option>
          <option value="Boutique">{t('positioningBoutique')}</option>
          <option value="Exotic">{t('positioningExotic')}</option>
          <option value="Niche">{t('positioningNiche')}</option>
        </select>
        <select
          className="form-select mb-2"
          value={form.recommendedTime}
          onChange={(e) => setForm({ ...form, recommendedTime: e.target.value })}
        >
          <option value="">{t('recommendedTime')}</option>
          <option value="Day">{t('recommendedDay')}</option>
          <option value="Evening">{t('recommendedEvening')}</option>
          <option value="Night">{t('recommendedNight')}</option>
        </select>
        <select
          className="form-select mb-2"
          value={form.targetAudience}
          onChange={(e) => setForm({ ...form, targetAudience: e.target.value })}
        >
          <option value="">{t('targetAudience')}</option>
          <option value="Beginners">{t('audienceBeginners')}</option>
          <option value="Experienced">{t('audienceExperienced')}</option>
          <option value="Universal">{t('audienceUniversal')}</option>
        </select>
        <h5 className="mt-3 mb-2">{t('passportExtended')}</h5>
        <textarea
          className="form-control mb-2"
          placeholder={t('extendedLabReports')}
          value={formatTags(form.extendedLabReports)}
          onChange={handleTagChange('extendedLabReports', 10)}
        />
        <textarea
          className="form-control mb-2"
          placeholder={t('extendedFullCannabinoidProfile')}
          value={form.extendedFullCannabinoidProfile}
          onChange={(e) =>
            setForm({ ...form, extendedFullCannabinoidProfile: e.target.value })
          }
        />
        <textarea
          className="form-control mb-2"
          placeholder={t('extendedSecondaryTerpenes')}
          value={formatTags(form.extendedSecondaryTerpenes)}
          onChange={handleTagChange('extendedSecondaryTerpenes', 12)}
        />
        <textarea
          className="form-control mb-2"
          placeholder={t('extendedCultivationNotes')}
          value={form.extendedCultivationNotes}
          onChange={(e) => setForm({ ...form, extendedCultivationNotes: e.target.value })}
        />
        <textarea
          className="form-control mb-2"
          placeholder={t('extendedInternalCommercialNotes')}
          value={form.extendedInternalCommercialNotes}
          onChange={(e) =>
            setForm({ ...form, extendedInternalCommercialNotes: e.target.value })
          }
        />
        <input className="form-control mb-2" placeholder={t('genetics')} value={form.genetics} onChange={(e) => setForm({ ...form, genetics: e.target.value })} />
        <textarea className="form-control mb-2" placeholder={t('description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {edit ? t('save') : t('addStrain')}
        </button>
        {saving && (
          <span className="ms-2 spinner-border spinner-border-sm" />
        )}
        {message && (
          <div className="mt-2">
            {message}
          </div>
        )}
        {edit && (
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setEdit(null);
              setForm({
                name: '',
                slug: '',
                genetics: '',
                geneticsType: '',
                geneticsDominance: '',
                description: '',
                shortDescription: '',
                thcPercent: '',
                cbdPercent: '',
                floweringWeeks: '',
                floweringCategory: '',
                yield: '',
                plantHeight: '',
                terpenes: [],
                aromaFlavor: [],
                effectDirection: '',
                effectSensations: [],
                status: '',
                uniqueTrait: '',
                positioning: '',
                recommendedTime: '',
                targetAudience: '',
                extendedLabReports: [],
                extendedFullCannabinoidProfile: '',
                extendedSecondaryTerpenes: [],
                extendedCultivationNotes: '',
                extendedInternalCommercialNotes: '',
                images: [],
              });
            }}
          >
            {t('cancel')}
          </button>
        )}
      </form>
      <div className="row">
        {filtered.map((s) => (
          <div
            key={s?._id || s?.slug || s?.name}
            className={`${mode === 'large' ? 'col-12 col-md-6' : 'col-12 col-md-6 col-lg-4'} mb-3`}
          >
            <StrainCard
              strain={s}
              isAdmin={true}
              onEdit={startEdit}
              onDelete={() => s?._id && del(s._id)}
              labelMode={labelMode}
            />
          </div>
        ))}
      </div>
      {!loading && !error && filtered.length === 0 && (
        <div className="alert alert-secondary" role="status">
          {t('noStrains')}
        </div>
      )}
    </div>
  );
}

export default function StrainsAdminPage() {
  const { t } = useLang();

  return (
    <StrainsAdminErrorBoundary
      fallbackMessage={t('strainsAdminCrashed')}
      retryLabel={t('retry')}
    >
      <StrainsAdminPageContent />
    </StrainsAdminErrorBoundary>
  );
}
